import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, MapPin, Phone, Mail, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CUSTOMER_TYPE, ORDER_TYPE, fmtDate, relDay, fmtHours, fmtNOK } from "@/lib/format";
import { OrderStatus, QuoteStatus, Chip } from "@/components/drift/StatusBadge";
import { PageHeader, Panel, EmptyState, LoadingRows, Field, KV } from "@/components/drift/common";
import { CompanyMark } from "@/components/drift/CompanyMark";
import { NewOrderDialog } from "@/components/drift/Dialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function NewLocationDialog({ customerId, open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", address: "", place: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Namn på anlegget manglar"); return; }
    setBusy(true);
    try {
      const r = await api.post("/drift/locations", { ...f, customer_id: customerId });
      toast.success("Anlegg oppretta");
      onOpenChange(false);
      setF({ name: "", address: "", place: "", description: "" });
      onCreated(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-location-dialog">
        <DialogHeader><DialogTitle className="font-display">Nytt anlegg / lokasjon</DialogTitle><DialogDescription>Ein stad der kunden har installasjonar vi følgjer opp.</DialogDescription></DialogHeader>
        <div className="grid gap-3">
          <Field label="Namn"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="T.d. Elektrolysehall B" data-testid="new-location-name-input" /></Field>
          <Field label="Adresse"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="new-location-address-input" /></Field>
          <Field label="Stad"><Input value={f.place} onChange={(e) => setF({ ...f, place: e.target.value })} placeholder="Husnes" /></Field>
          <Field label="Beskriving"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Avbryt</Button><Button onClick={submit} disabled={busy} className="bg-brand text-white hover:bg-brand-dark" data-testid="new-location-submit-button">{busy ? "Lagrar …" : "Opprett anlegg"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);

  const load = useCallback(() => api.get(`/drift/customers/${id}`).then((r) => setC(r.data)).catch(() => setErr(true)), [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <EmptyState title="Fann ikkje kunden" action={<Button variant="outline" asChild><Link to="/drift/kundar">Tilbake</Link></Button>} />;
  if (!c) return <LoadingRows n={8} />;

  const active = c.orders.filter((o) => ["ny", "planlagt", "paagaar"].includes(o.status));
  const totalHours = c.orders.reduce((s, o) => s + (o.totals?.hours || 0), 0);

  return (
    <div data-testid="customer-detail-page">
      <Link to="/drift/kundar" className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Kundar</Link>
      <PageHeader eyebrow={CUSTOMER_TYPE[c.type]} title={<span className="flex items-center gap-4"><CompanyMark name={c.name} brand={c.brand} type={c.type} size="xl" /><span>{c.name}</span></span>} subtitle={c.address}
        actions={<>
          <Button variant="outline" onClick={() => setLocOpen(true)} data-testid="customer-new-location-button"><Plus className="mr-1.5 h-4 w-4" /> Nytt anlegg</Button>
          <Button onClick={() => setOrderOpen(true)} className="bg-brand text-white hover:bg-brand-dark" data-testid="customer-new-order-button"><Plus className="mr-1.5 h-4 w-4" /> Nytt oppdrag</Button>
        </>} />

      <div className="grid gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Panel title={`Anlegg · ${c.locations.length}`} bodyClass="p-0" testid="customer-locations-panel">
            {c.locations.length === 0 ? <div className="p-4"><EmptyState icon={Building2} title="Ingen anlegg" text="Opprett eit anlegg for å byggje historikk." /></div> : (
              <ul className="divide-y divide-border">
                {c.locations.map((l) => (
                  <li key={l.id}>
                    <Link to={`/drift/anlegg/${l.id}`} className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/50" data-testid="customer-location-row">
                      <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-accent"><MapPin className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1"><span className="block truncate font-medium">{l.name}</span><span className="block truncate text-xs text-muted-foreground">{l.address}{l.place ? ` · ${l.place}` : ""}</span></span>
                      <span className="hidden text-xs text-muted-foreground sm:block">{l.installation_count} installasjonar</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title={`Oppdrag · ${c.orders.length}`} bodyClass="p-0" testid="customer-orders-panel">
            {c.orders.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Ingen oppdrag enno.</p> : (
              <ul className="divide-y divide-border">
                {c.orders.map((o) => (
                  <li key={o.id}>
                    <Link to={`/drift/oppdrag/${o.id}`} className="grid grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50 sm:grid-cols-[90px_1fr_120px_auto]" data-testid="customer-order-row">
                      <span className="hidden font-mono text-xs text-muted-foreground sm:block">{o.ref}</span>
                      <span className="min-w-0"><span className="block truncate text-sm font-medium">{o.title}</span><span className="block truncate text-xs text-muted-foreground">{ORDER_TYPE[o.type]} · {o.location_name} · {o.scheduled_date ? relDay(o.scheduled_date) : "ikkje planlagt"}{o.assigned_name ? ` · ${o.assigned_name}` : ""}</span></span>
                      <span className="hidden text-right font-mono text-xs tabular text-muted-foreground sm:block">{fmtHours(o.totals?.hours)}</span>
                      <OrderStatus status={o.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {c.quotes.length > 0 && (
            <Panel title={`Tilbod · ${c.quotes.length}`} bodyClass="p-0">
              <ul className="divide-y divide-border">
                {c.quotes.map((q) => (
                  <li key={q.id} className="flex items-center gap-3 px-4 py-3 text-sm"><span className="font-mono text-xs text-muted-foreground">{q.ref}</span><span className="flex-1 truncate">{q.title}</span><span className="font-mono tabular">{q.amount ? fmtNOK(q.amount) : "–"}</span><QuoteStatus status={q.status} /></li>
                ))}
              </ul>
            </Panel>
          )}
        </div>

        <div className="space-y-4">
          <Panel title="Kontakt" testid="customer-contact-panel">
            <div className="divide-y divide-border">
              <KV k="Kontaktperson" v={c.contact_name || "–"} />
              <KV k="Telefon" v={c.phone ? <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-brand"><Phone className="h-3.5 w-3.5" />{c.phone}</a> : "–"} mono />
              <KV k="E-post" v={c.email ? <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-brand"><Mail className="h-3.5 w-3.5" />{c.email}</a> : "–"} />
              <KV k="Org.nr" v={c.org_nr || "–"} mono />
              <KV k="Kunde sidan" v={fmtDate(c.created_at)} />
            </div>
          </Panel>
          <Panel title="Nøkkeltal">
            <div className="grid grid-cols-3 gap-3">
              {[["Aktive", active.length], ["Totalt", c.orders.length], ["Timar", Math.round(totalHours)]].map(([l, v]) => (
                <div key={l} className="rounded-sm bg-accent p-3"><p className="text-[11px] text-muted-foreground">{l}</p><p className="mt-1 font-mono text-2xl tabular">{v}</p></div>
              ))}
            </div>
          </Panel>
          {c.inquiries.length > 0 && (
            <Panel title="Henvendingar" bodyClass="p-0">
              <ul className="divide-y divide-border">{c.inquiries.map((i) => <li key={i.id}><Link to={`/drift/henvendingar/${i.id}?status=alle`} className="block px-4 py-2.5 text-sm hover:bg-accent/50"><span className="font-mono text-xs text-muted-foreground">{i.ref}</span> <span className="line-clamp-1">{i.message}</span></Link></li>)}</ul>
            </Panel>
          )}
        </div>
      </div>

      <NewLocationDialog customerId={c.id} open={locOpen} onOpenChange={setLocOpen} onCreated={(l) => navigate(`/drift/anlegg/${l.id}`)} />
      <NewOrderDialog open={orderOpen} onOpenChange={setOrderOpen} customerId={c.id} />
    </div>
  );
}
